import React, { Fragment, useEffect, useState } from "react";
import { ShowHide } from "components/layout";
import { AlertUnsavedData, NotificationInline, NotificationToaster } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import { DefaultEventTypeProcessModel, DefaultSignoffOptions, FailedOnlySignoffOption } from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { FleetService } from "services";
import { Intent } from "@blueprintjs/core";
import { FormRadio, FormSwitch, FormTextArea } from "components/form-fields";
import { ButtonSave } from "components/buttons";
import { useNavigate } from "react-router-dom";

export function EventTypeProcess({
    model = DefaultEventTypeProcessModel,
    forceLoad = false,
    isInWizardMode = true,
    saving = false,
    globalLoading = false,
    onValidationUpdate = () => { },
    onModelUpdate = () => { return DefaultEventTypeProcessModel; },
    onSave = () => { },
    onForceLoadFinished = () => { },
    specialProcessingKey = null,
    failStatusSettings = { hasFailStatusOptionsSelected: false, failStatusOptionsSelectedPages: [] },
    erMaintenanceLocked = false
}) {
    var navigate = useNavigate();

    const step = 2;
    const fmsManageAction = "FMS:Manage";
    const driverAppApproveAction = "DriverApp:Approve";
    const userHasFmsManageAction = UserHasAccess(fmsManageAction);
    const userHasDriverAppApproveAction = UserHasAccess(driverAppApproveAction);
        
    const [loading, setLoading] = useState(true);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
    const [optionsFetched, setOptionsFetched] = useState(false);

    const scheduledStatus = "SCHEDULED";
    const allowedInitialEventStatuses = ["PLANNED", scheduledStatus, "INPROGRESS"];

    const [originalProcessModel, setOriginalProcessModel] = useState(DefaultEventTypeProcessModel);
    const [processModel, setProcessModel] = useState(DefaultEventTypeProcessModel)

    const [isValid, processModelErrors, validateProcessModel] = useValidation();
    const isProcessModelDirty = useDirtyData(originalProcessModel, processModel);

    const [initialStatuses, setInitialStatuses] = useState([]);
    const [signoffOptions, setSignoffOptions] = useState(DefaultSignoffOptions);
    const [failedDisabled, setFailedDisabled] = useState(false);

    // Field names
    const initialEventStatusIdFieldName = "initialEventStatusId";
    const failStatusEnabledFieldName = "failStatusEnabled";
    const signoffRequiredFieldName = "requiresSignoff";
    const requiresSignoffValueFieldName = "requiresSignoffValue";
    const requiresSignoffFailedOnlyFieldName = "requiresSignoffFailedOnly";
    const signoffHelperTextFieldName = "signoffHelperText";
    const signoffRestrictedFieldName = "signoffRestricted";
    const signoffSecondEnabledFieldName = "signoffSecondEnabled";
    const signoffSecondRestrictedFieldName = "signoffSecondRestricted";
    const employeeEditFieldName = "employeeEdit";
    // Fields names END

    const switchFields = [failStatusEnabledFieldName, signoffRestrictedFieldName, signoffSecondEnabledFieldName, signoffSecondRestrictedFieldName, employeeEditFieldName];

    // When the event type is ER maintenance PMI, certain fields are locked to comply with DVSA Earned Recognition requirements.
    const erMaintenanceLockMessage = "Field locked to comply with requirements of the DVSA Earned Recognition scheme";

    useEffect(load, [model, forceLoad]);

    function load() {
        if (!userHasFmsManageAction || (forceLoad === false && initialLoadCompleted)) {
            return;
        }

        setModel(prev => { return {...prev, ...model}; });

        if (
            !isInWizardMode &&
            (
                model.initialEventStatusId !== DefaultEventTypeProcessModel.initialEventStatusId ||
                model.failStatusEnabled !== DefaultEventTypeProcessModel.failStatusEnabled ||
                model.requiresSignoff !== DefaultEventTypeProcessModel.requiresSignoff ||
                model.requiresSignoffValue !== DefaultEventTypeProcessModel.requiresSignoffValue ||
                model.signoffHelperText !== DefaultEventTypeProcessModel.signoffHelperText ||
                model.requiresSignoffFailedOnly !== DefaultEventTypeProcessModel.requiresSignoffFailedOnly ||
                model.signoffRestricted !== DefaultEventTypeProcessModel.signoffRestricted
            )
        ) {
            setOriginalProcessModel(model);
        }

        setInitialLoadCompleted(true);

        fetchOptions();
    
        if (forceLoad) {
            onForceLoadFinished();
        }
    }

    function fetchOptions() {
        if (optionsFetched) {
            return;
        }

        setLoading(true);

        FleetService.getEventOptions()
            .then((response) => {
                setOptionsFetched(true);
                const filterStatuses = response.eventStatuses
                    .filter(status => allowedInitialEventStatuses.includes(status.key))
                    .map(status => { 
                        return { value: status.id, label: status.name }; 
                    });

                setInitialStatuses(filterStatuses);
            })
            .catch(() => {
                NotificationToaster.show(Intent.WARNING, 'Event options could not be loaded. Please try again later.');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        if (specialProcessingKey) {
            if (specialProcessingKey.toUpperCase() === "MOT" || specialProcessingKey.toUpperCase() === "TAX" || specialProcessingKey.toUpperCase() === "WALKAROUND" || specialProcessingKey.toUpperCase() === "LOGUKVISIT") {
                setFailedDisabled(true);
                return;
            }
        }
        setFailedDisabled(false);
    }, [specialProcessingKey]);

    useEffect(() => {
        setLoading(globalLoading);
    }, [globalLoading]);

    // publish out validation changes if in wizard mode
    useEffect(() => {
        if (!isInWizardMode) {
            return;
        }

        onValidationUpdate(step, isValid);
    }, [isValid])

    useEffect(validate, [processModel])

    function validate() {
        let validationRules = [];

        if (showEventSignoffFields()) {
            validationRules.push(
                { 
                    fieldName: signoffHelperTextFieldName,
                    maxLength: 1000 
                }
            );
        }

        validateProcessModel(validationRules, processModel);
    }

    // Use this to handle input value changes
    function onValueChange(fieldName, item) {
        const value = switchFields.includes(fieldName) ? item.currentTarget.checked : item.currentTarget.value;

        const clonedModel = {
            ...processModel,
            [fieldName]: value
        };

        // if we are setting fail status enabled and the given value is false then reset requires signoff
        if (fieldName === failStatusEnabledFieldName && value === false && processModel.requiresSignoffValue === "3") {
            clonedModel[signoffRequiredFieldName] = false;
            clonedModel[requiresSignoffValueFieldName] = "1";
            clonedModel[requiresSignoffFailedOnlyFieldName] = false;
        }

        setProcessModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }

    function onRadioValueChange(fieldName, item) {
        const clonedModel = {
            ...processModel,
            [fieldName]: Number.parseInt(item.currentTarget.value)
        };

        setProcessModel(clonedModel);

        onModelUpdate(step, clonedModel);
    }

    useEffect(() => {
        const tempSignoffOptions = [...DefaultSignoffOptions];

        if (processModel.failStatusEnabled) {
            tempSignoffOptions.push(FailedOnlySignoffOption);
        }

        setSignoffOptions(tempSignoffOptions);

    }, [processModel.failStatusEnabled]);

    function onRequiresSignoffValueChange(item) {
        const selectedValue = signoffOptions.find(option => option.value === item.currentTarget.value);

        const clonedModel = {
            ...processModel,
            [signoffRequiredFieldName]: selectedValue.signoffRequired,
            [requiresSignoffValueFieldName]: selectedValue.value,
            // Default this to false and set it beneath if conditions are met
            [requiresSignoffFailedOnlyFieldName]: false
        };

        if (processModel.failStatusEnabled && selectedValue.value === "3") {
            clonedModel[requiresSignoffFailedOnlyFieldName] = true;
        }

        setProcessModel(clonedModel);

        onModelUpdate(step, clonedModel);
    }

    function setModel(changeModel) {
        setOriginalProcessModel(changeModel);
        setProcessModel(changeModel);
    }

    function showEventSignoffFields() {
        return processModel.requiresSignoffValue === "2" || processModel.requiresSignoffValue === "3";
    }

    function showSecondSignoffRestricted() {
        return processModel.signoffSecondEnabled === true;
    }

    function onConfirmLeave(redirectToPath) {
        setModel(originalProcessModel);
        onModelUpdate(step, originalProcessModel);

        setTimeout(() => navigate(redirectToPath));
    }

    function getFailStatusNotificationText() {
        return (
            <Fragment>
                <p>The event fail status is disabled as this event type has a fail status option selected ("Only required for failed events") on the following pages: </p>
                <ul>
                    {failStatusSettings.failStatusSelectedPages.map(page => (<li key={page}>{page}</li>))}
                </ul>
                <p>To disable the event fail status please set all of the above settings to "Not Required", "Required" or turn off "Only required for failed events".</p>
            </Fragment>
        );
    }

    return (
        <UserAccess perform={fmsManageAction}
            yes={() => (
                <Fragment>
                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <Fragment>
                                <h2>Event Process</h2>
                                <AlertUnsavedData isDirty={isProcessModelDirty} onConfirmLeave={onConfirmLeave} />
                            </Fragment>
                        )}
                    />

                    <FormRadio
                        disabled={saving || !!specialProcessingKey}
                        options={initialStatuses}
                        onChange={(item) => onRadioValueChange(initialEventStatusIdFieldName, item)}
                        headingText="Please select the initial status of this event"
                        selectedValue={processModel.initialEventStatusId}
                        dangerHelperText={processModelErrors[initialEventStatusIdFieldName]}
                        loading={loading}
                        id="input-initial-event-status"
                    />

                    <h3>Event Fail Status</h3>

                    <NotificationInline 
                        allowClose={false} 
                        text={getFailStatusNotificationText()} 
                        intent="warning" 
                        show={failStatusSettings.hasAnyFailedOnlyOptionsSelected && !failedDisabled} 
                    />

                    <FormSwitch
                        disabled={saving || failStatusSettings.hasAnyFailedOnlyOptionsSelected || failedDisabled}
                        loading={loading}
                        headingText="Enable event fail status"
                        checked={processModel.failStatusEnabled}
                        onChange={(item) => onValueChange(failStatusEnabledFieldName, item)}
                    />

                    <h3>Event Sign-off</h3>

                    <FormRadio
                        disabled={saving || erMaintenanceLocked}
                        options={signoffOptions}
                        onChange={onRequiresSignoffValueChange}
                        headingText="Please select the circumstances where sign-off is required. Signoff will only show if it is required, or required for failed events and the event is failed."
                        selectedValue={processModel.requiresSignoffValue}
                        dangerHelperText={processModelErrors[requiresSignoffValueFieldName]}
                        loading={loading}
                        id="input-requires-signoff"
                    />

                    <NotificationInline
                        allowClose={false}
                        text={erMaintenanceLockMessage}
                        intent="warning"
                        show={erMaintenanceLocked}
                    />

                    <ShowHide
                        evaluator={showEventSignoffFields()}
                        show={(
                            <Fragment>
                                <FormTextArea
                                    maxLength={1000}
                                    disabled={saving || erMaintenanceLocked}
                                    loading={loading}
                                    onChange={(item) => onValueChange(signoffHelperTextFieldName, item)}
                                    headingText="Sign-off text"
                                    value={processModel.signoffHelperText}
                                    dangerHelperText={processModelErrors[signoffHelperTextFieldName]}
                                />

                                <NotificationInline
                                    allowClose={false}
                                    text={erMaintenanceLockMessage}
                                    intent="warning"
                                    show={erMaintenanceLocked}
                                />

                                <FormSwitch
                                    disabled={saving}
                                    loading={loading}
                                    headingText="Restrict sign-off to event sign-off contacts only"
                                    helperText="Event sign-off contacts can be set up on a per location basis in the Location Management pages under Vision Administration on the sidebar"
                                    checked={processModel.signoffRestricted}
                                    onChange={(item) => onValueChange(signoffRestrictedFieldName, item)}
                                />

                                <FormSwitch
                                    disabled={saving || erMaintenanceLocked}
                                    loading={loading}
                                    headingText="Require second sign-off"
                                    checked={processModel.signoffSecondEnabled}
                                    onChange={(item) => onValueChange(signoffSecondEnabledFieldName, item)}
                                />

                                <NotificationInline
                                    allowClose={false}
                                    text={erMaintenanceLockMessage}
                                    intent="warning"
                                    show={erMaintenanceLocked}
                                />

                                <ShowHide
                                    evaluator={showSecondSignoffRestricted()}
                                    show={(
                                        <FormSwitch
                                            disabled={saving}
                                            loading={loading}
                                            headingText="Restrict second sign-off to event sign-off contacts only"
                                            helperText="Event sign-off contacts can be set up on a per location basis in the Location Management pages under Vision Administration on the sidebar"
                                            checked={processModel.signoffSecondRestricted}
                                            onChange={(item) => onValueChange(signoffSecondRestrictedFieldName, item)}
                                        />
                                    )}
                                />
                            </Fragment>
                        )}
                    />

                    <ShowHide
                        evaluator={userHasDriverAppApproveAction}
                        show={(
                            <>
                                <h3>Event on Vision App</h3>
                                <FormSwitch
                                    disabled={saving}
                                    loading={loading}
                                    headingText="Enable employee to alter this event type on the app"
                                    helperText="This will allow non-Vision users to make changes to events of this type on the driver app."
                                    checked={processModel.employeeEdit}
                                    onChange={(item) => onValueChange(employeeEditFieldName, item)}
                                />
                            </>
                        )}
                    />

                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <ButtonSave
                                loading={loading}
                                onClick={() => onSave("process")}
                                disabled={saving}
                                simpleDisabled={!isProcessModelDirty || !isValid}
                            />	
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EventTypeProcess.propTypes = {
    model: PropTypes.object,
    forceLoad: PropTypes.bool,
    isInWizardMode: PropTypes.bool,
    saving: PropTypes.bool.isRequired,
    globalLoading: PropTypes.bool,
    onValidationUpdate: PropTypes.func,
    onModelUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onForceLoadFinished: PropTypes.func,
    specialProcessingKey: PropTypes.string,
    failStatusSettings: PropTypes.object,
    erMaintenanceLocked: PropTypes.bool,
};